import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from '@/lib/i18n';
import { SEOHead } from '@/components/SEOHead';
import { productSchema, breadcrumbSchema, lowestPrice } from '@/lib/seo';
import {
  Cloud, Shield, Zap, Check, ExternalLink,
  HardDrive, Smartphone, FileText, Lock, Users, Share2, RefreshCw,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const nextcloudPlans = [
  { name: 'Nextcloud 50 GB',   storage: '50 GB',    price: '2,49', oldPrice: null,   pid: 513 },
  { name: 'Nextcloud 500 GB',  storage: '500 GB',   price: '5,00', oldPrice: null,   pid: 514, popular: true },
  { name: 'Nextcloud 1000 GB', storage: '1,000 GB', price: '7,50', oldPrice: '10,00',pid: 515 },
  { name: 'Nextcloud 2000 GB', storage: '2,000 GB', price: '20,00',oldPrice: null,   pid: 516 },
];

const planFeatureKeys = [
  'Unlimited user accounts',
  'Full admin rights',
  'Easily expandable',
  'Prepaid – no contract',
];

const useCaseKeys = [
  { key: 'storage', icon: HardDrive },
  { key: 'share',   icon: Share2 },
  { key: 'office',  icon: FileText },
  { key: 'mobile',  icon: Smartphone },
  { key: 'sync',    icon: RefreshCw },
  { key: 'privacy', icon: Lock },
];

const faqKeys = [1, 2, 3, 4];

const NextcloudCard = ({ plan, t }) => (
  <Card className={`h-full relative flex flex-col ${plan.popular ? 'border-primary/50 bg-primary/5' : ''}`}>
    {plan.popular && (
      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">{t('common.popular')}</Badge>
    )}
    <CardHeader>
      <CardTitle className="text-base">{plan.name}</CardTitle>
      <CardDescription>{plan.storage} {t('webhosting.nc_storage')}</CardDescription>
    </CardHeader>
    <CardContent className="flex flex-col flex-1">
      <ul className="space-y-1.5 mb-6 flex-1">
        {planFeatureKeys.map((feature, i) => (
          <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
            <Check className="w-4 h-4 text-green-500 shrink-0" />
            {feature}
          </li>
        ))}
      </ul>
      <div className="flex items-baseline gap-2 mb-4">
        {plan.oldPrice && (
          <span className="text-muted-foreground line-through text-sm">€{plan.oldPrice}</span>
        )}
        <span className="text-3xl font-black text-primary">€{plan.price}</span>
        <span className="text-muted-foreground text-sm">{t('common.perMonth')}</span>
      </div>
      <a href={`https://deinserverhost.de/store/cart.php?a=add&pid=${plan.pid}`} target="_blank" rel="noopener noreferrer">
        <Button className="w-full bg-primary hover:bg-primary/90" data-testid={`order-nc-${plan.pid}`}>
          {t('common.buyNow')} <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </a>
    </CardContent>
  </Card>
);

export default function WebhostingNextcloudPage() {
  const { t } = useTranslation();

  return (
    <Layout>
      <SEOHead
        title="Nextcloud Hosting – Your Private Cloud"
        description={`Your own Nextcloud from €${lowestPrice(nextcloudPlans.map((p) => p.price))}/month: files, calendar and contacts – private and GDPR-compliant, 50 GB to 2 TB storage. No contract.`}
        canonical="/webhosting/nextcloud"
        schema={[
          productSchema({
            name: 'Nextcloud Hosting',
            description: 'Managed Nextcloud hosting for files, calendar and contacts – private and GDPR-compliant, 50 GB to 2 TB storage.',
            path: '/webhosting/nextcloud',
            offers: nextcloudPlans.map((plan) => ({ name: plan.name, price: plan.price, period: 'month' })),
          }),
          breadcrumbSchema([{ name: 'Web Hosting', path: '/webhosting' }, { name: 'Nextcloud Hosting', path: '/webhosting/nextcloud' }]),
        ]}
      />
      <div className="section-spacing">
        <div className="container-main">

          {/* Hero */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
              <Badge className="mb-4 bg-green-500/20 text-green-400 border border-green-500/30">
                {t('webhosting.nc_badge')}
              </Badge>
              <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
                {t('webhosting.nc_title1')}<br /><span className="text-gradient">{t('webhosting.nc_title2')}</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-6">{t('webhosting.nc_desc')}</p>
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { icon: Shield, key: 'nc_badge_gdpr' },
                  { icon: Lock,   key: 'nc_badge_privacy' },
                  { icon: Users,  key: 'nc_badge_users' },
                ].map(({ icon: Icon, key }) => (
                  <div key={key} className="flex items-center gap-2 px-3 py-1.5 rounded-full glass text-sm">
                    <Icon className="w-4 h-4 text-primary" />{t(`webhosting.${key}`)}
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Link to="/webhosting/ssd">
                  <Button variant="outline" size="sm" className="border-primary/30 hover:border-primary">
                    <HardDrive className="w-4 h-4 mr-2" />SSD Hosting
                  </Button>
                </Link>
                <Link to="/webhosting/nvme">
                  <Button variant="outline" size="sm" className="border-primary/30 hover:border-primary">
                    <Zap className="w-4 h-4 mr-2" />NVMe Hosting
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="flex justify-center"
            >
              <img src="/images/penguin/server_penguin_500.png" alt="DeinServerHost Mascot" title="DeinServerHost Mascot" className="w-56 md:w-72 drop-shadow-2xl" />
            </motion.div>
          </div>

          {/* Plans */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-16">
            <h2 className="text-2xl font-bold text-center mb-3">{t('webhosting.nc_plansTitle')}</h2>
            <p className="text-center text-muted-foreground mb-8">{t('webhosting.nc_plansSubtitle')}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {nextcloudPlans.map((plan) => (
                <NextcloudCard key={plan.name} plan={plan} t={t} />
              ))}
            </div>
          </motion.div>

          {/* Use Cases */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-16">
            <h2 className="text-2xl font-bold text-center mb-3">{t('webhosting.nc_usecasesTitle')}</h2>
            <p className="text-center text-muted-foreground mb-8">{t('webhosting.nc_usecasesSubtitle')}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {useCaseKeys.map(({ key, icon: Icon }) => (
                <div key={key} className="flex items-start gap-4 p-4 glass rounded-xl border border-border">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{t(`webhosting.nc_use_${key}_title`)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{t(`webhosting.nc_use_${key}_desc`)}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* FAQ */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="p-8 glass rounded-2xl border border-border mb-16">
            <h2 className="text-2xl font-bold text-center mb-8">{t('webhosting.nc_faqTitle')}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {faqKeys.map((n) => (
                <div key={n}>
                  <div className="font-semibold text-sm mb-1">{t(`webhosting.nc_faq_q${n}`)}</div>
                  <div className="text-sm text-muted-foreground">{t(`webhosting.nc_faq_a${n}`)}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="text-center p-8 glass rounded-2xl">
            <h2 className="text-2xl font-bold mb-4">{t('webhosting.nc_ctaTitle')}</h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">{t('webhosting.nc_ctaDesc')}</p>
            <a href="https://deinserverhost.de/store/cart.php?a=add&pid=513" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover">
                {t('webhosting.nc_ctaBtn')} <ExternalLink className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>

        </div>
      </div>
    </Layout>
  );
}
